import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import {
  AuthMethod,
  UserAuthError,
  UserAuthResponseData,
} from 'src/app/@data-services/user-auth/user-auth.data-model';
import { UserAuthDataService } from 'src/app/@data-services/user-auth/user-auth.data-service';
import { MixpanelService } from 'src/app/@services/mixpanel.service';
import { AuthStore } from 'src/app/@core/auth/stores/auth-store';
import { AuthStateService } from 'src/app/@stores/auth-state.service';
import { EventConfigStateService } from 'src/app/@stores/event-config-state.service';
import { ExternalRegistrationStateService } from 'src/app/@stores/external-registration-state.service';
import {
  ENROLL_EVENT_REPORT,
  ENROLLED_USER_SESSION,
  REPORTS_EVENT_ROUTE_SLUG,
} from 'src/app/@constants/local-storage-key-constants';
import { removeLocalStorageItem } from 'src/app/@utils/local-storage-util';

export interface AuthResult {
  success: boolean;
  error?: UserAuthError;
}

@Injectable({
  providedIn: 'root',
})
export class UserAuthService {
  private readonly _authDataService = inject(UserAuthDataService);
  private readonly _authStateService = inject(AuthStateService);
  private readonly _authStore = inject(AuthStore);
  private readonly _eventConfigStateService = inject(EventConfigStateService);
  private readonly _mixpanelService = inject(MixpanelService);
  private readonly _externalRegistrationStateService = inject(
    ExternalRegistrationStateService
  );

  authenticate(
    eventNameIdentifier: string,
    authMethod: AuthMethod,
    identifier: string,
    extras?: { username?: string; password?: string }
  ): Observable<AuthResult> {
    this._authStateService.setEventNameIdentifier(eventNameIdentifier);
    this._authStateService.setStatus('authenticating');

    return this._authDataService
      .validateUser(eventNameIdentifier, authMethod, identifier, extras)
      .pipe(
        tap((response: UserAuthResponseData) => {
          this._handleAuthResponse(response, {
            // Username/password is not Cognito — never inherit a stale Cognito
            // token expiry or refresh will immediately invalidate the session.
            useCognitoExpiry: authMethod !== 'usernamePassword',
          });
        }),
        map((response: UserAuthResponseData) => ({
          success: response.success && response.resolved,
          error: response.error,
        })),
        catchError(() => {
          const networkError: UserAuthError = {
            code: 'NETWORK_ERROR',
            message:
              'Unable to connect to authentication service. Please try again.',
          };
          this._authStateService.setError(networkError);
          return of({ success: false, error: networkError });
        })
      );
  }

  authenticateWithEmail(
    eventNameIdentifier: string,
    email: string
  ): Observable<AuthResult> {
    return this.authenticate(eventNameIdentifier, 'email', email);
  }

  authenticateWithToken(
    eventNameIdentifier: string,
    token: string
  ): Observable<AuthResult> {
    return this.authenticate(eventNameIdentifier, 'token', token);
  }

  authenticateWithRegistrationId(
    eventNameIdentifier: string,
    registrationId: string
  ): Observable<AuthResult> {
    return this.authenticate(
      eventNameIdentifier,
      'registrationId',
      registrationId
    );
  }

  authenticateWithUsernamePassword(
    eventNameIdentifier: string,
    username: string,
    password: string
  ): Observable<AuthResult> {
    return this.authenticate(
      eventNameIdentifier,
      'usernamePassword',
      username,
      { username, password }
    );
  }

  isAuthenticationRequired(): boolean {
    const config = this._eventConfigStateService.$eventConfig();
    if (!config) {
      return false; // Default to not requiring authentication
    }

    // KnowledgeHubConfig takes precedence if present
    if (config.KnowledgeHubConfig) {
      return config.KnowledgeHubConfig.RequiredLogin ?? false;
    }

    return config.Features.RequireAuthentication ?? false;
  }

  /** True when Knowledge Hub uses shared username/password instead of email OTP. */
  isUsernamePasswordLogin(): boolean {
    const khc =
      this._eventConfigStateService.$eventConfig()?.KnowledgeHubConfig;
    if (!khc?.RequiredLogin) {
      return false;
    }
    const loginType =
      khc.LoginType ?? (khc as { loginType?: string }).loginType;
    return loginType === 'USERNAME/PASSWORD';
  }

  isEmailAuthAllowed(): boolean {
    const config = this._eventConfigStateService.$eventConfig();
    if (!config) {
      return true;
    }
    return config.Features.AllowEmailAuth ?? true;
  }

  isTokenAuthAllowed(): boolean {
    const config = this._eventConfigStateService.$eventConfig();
    if (!config) {
      return true;
    }
    return config.Features.AllowTokenAuth ?? true;
  }

  checkExistingAuth(eventNameIdentifier: string): boolean {
    return this._authStateService.isValidForEvent(eventNameIdentifier);
  }

  logout(): void {
    this._authStateService.reset();
    this._mixpanelService.reset();
    this._externalRegistrationStateService.reset();
    removeLocalStorageItem(ENROLL_EVENT_REPORT);
    removeLocalStorageItem(ENROLLED_USER_SESSION);
    removeLocalStorageItem(REPORTS_EVENT_ROUTE_SLUG);
  }

  private _handleAuthResponse(
    response: UserAuthResponseData,
    options?: { useCognitoExpiry?: boolean }
  ): void {
    if (response.success && response.resolved && response.data) {
      const useCognitoExpiry = options?.useCognitoExpiry !== false;
      const tokenExpiry = useCognitoExpiry
        ? (this._authStore.$sessionExpiry() ?? undefined)
        : null;
      this._authStateService.setUser(response.data, tokenExpiry);

      // Sync with MixpanelService for tracking
      if (response.data.email) {
        this._mixpanelService.setUserInfo(response.data.email);
      }
      if (response.data.registrationId) {
        this._externalRegistrationStateService.setRegistrationId(
          response.data.registrationId
        );
      }

      return;
    }

    if (response.error) {
      const status = this._mapErrorCodeToStatus(response.error.code);
      this._authStateService.setError(response.error, status);
      return;
    }

    // Fallback for unexpected response format
    const unknownError: UserAuthError = {
      code: 'USER_NOT_FOUND',
      message: 'Unable to verify your identity. Please try again.',
    };
    this._authStateService.setError(unknownError);
  }

  private _mapErrorCodeToStatus(
    code: string
  ): 'unresolved' | 'blocked' | 'no_entitlement' | 'error' {
    switch (code) {
      case 'BLOCKED_USER':
        return 'blocked';
      case 'NO_ENTITLEMENT':
      case 'BADGE_NOT_ALLOWED': // KnowledgeHub: badge type not allowed
        return 'no_entitlement';
      case 'USER_NOT_FOUND':
      case 'MULTIPLE_MATCHES':
        return 'unresolved';
      default:
        return 'error';
    }
  }
}
